import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { sendEmailVerification, onAuthStateChanged, signOut } from 'firebase/auth';
import logo from '../images/logo1.png';
import './styles/verifyEmail.css';

const VerifyEmail = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [checkingStatus, setCheckingStatus] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
      
      // If user is verified or there is no user, redirect to login
      if (!user) {
        navigate('/login');
      } else if (user.emailVerified) {
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const checkEmailVerification = async () => {
    if (!auth.currentUser) return;
    
    try {
      setCheckingStatus(true);
      setMessage({ text: '', type: '' });
      
      // Reload the user to check if emailVerified is updated
      await auth.currentUser.reload();
      
      if (auth.currentUser.emailVerified) {
        setMessage({ text: 'Your email has been verified! Redirecting...', type: 'success' });
        setTimeout(() => navigate('/'), 2000);
      } else {
        setMessage({ text: 'Your email is not verified yet. Check your inbox and spam folder.', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'An error occurred: ' + error.message, type: 'error' });
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleResendEmail = async () => {
    if (!auth.currentUser) return;
    
    try {
      setResendLoading(true);
      setMessage({ text: '', type: '' });
      
      await sendEmailVerification(auth.currentUser);
      setMessage({ text: 'A new verification email has been sent to your email.', type: 'success' });
    } catch (error) {
      setMessage({ text: 'Could not send verification email: ' + error.message, type: 'error' });
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      setMessage({ text: 'Error during logout: ' + error.message, type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="verify-container">
        <div className="verify-card">
          <div className="loading-spinner" style={{width: "30px", height: "30px", margin: "0 auto 1rem"}}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null; // This is handled by the useEffect redirect
  }

  return (
    <div className="verify-container">
      <div className="verify-card">
        <img src={logo} alt="Nuvik.io Logo" className="verify-logo" />
        
        <i className="fas fa-envelope-open-text verify-email-icon"></i>
        
        <h1>Verify your email</h1>
        
        <p>
          We have sent a verification email to: 
        </p>
        <div className="email-highlight">{currentUser?.email}</div>
        
        <p>You need to verify your email to use Nuvik.io. Click the link in the email to verify your account.</p>
        
        {message.text && (
          <div className={`${message.type === 'success' ? 'success-message' : 'error-message'}`}>
            {message.text}
          </div>
        )}
        
        <button 
          className="verify-button" 
          onClick={checkEmailVerification}
          disabled={checkingStatus}
        >
          {checkingStatus ? (
            <>
              <span className="loading-spinner"></span>
              Checking...
            </>
          ) : (
            'I have verified my email'
          )}
        </button>
        
        <div className="divider"></div>
        
        <div className="action-links">
          <button 
            className="resend-link" 
            onClick={handleResendEmail}
            disabled={resendLoading}
          >
            {resendLoading ? (
              <>
                <span className="loading-spinner"></span>
                Sending...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i>
                Resend verification email
              </>
            )}
          </button>
          
          <button 
            className="logout-link" 
            onClick={handleLogout}
          >
            <i className="fas fa-sign-out-alt"></i>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
